import React, { useEffect, useState } from "react";
import { useBrandStore } from "../store/brandStore";
import { Button, Table, Card, Row, Col, Form, Empty } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import { RFMTier, RFMType } from "../constants/enums";
import { ColumnsType } from "antd/es/table";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutePaths } from "../routes/route";
import { BrandRFMProfile } from "../models/brandRFMProfile";
import { brandRFMProfileService } from "../services/brandRFMProfileService";
import { useBrandRFMProfileStore } from "../store/brandRFMProfileStore";
import { brandRFMSettingService } from "../services/brandRFMSettingService";
import {
  BrandRFMSetting,
  DefaultBrandRFMSetting,
} from "../models/brandRFMSetting";

interface TierData {
  tier: number;
  baseStart: number;
  baseEnd: number;
}

const EditRFMProfile: React.FC = () => {
  // =====================
  // State Declarations
  // =====================`

  // Hooks & Store
  const { brandId } = useBrandStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBrandRFMProfile, setSelectedBrandRFMProfile } =
    useBrandRFMProfileStore();

  // UI & State Management
  const [loading, setLoading] = useState(false);
  // const validateMessages = {
  //   required: "${label} is required!",
  //   types: {
  //     email: "${label} is not a valid email!",
  //     number: "${label} is not a valid number!",
  //   },
  //   number: {
  //     range: "${label} must be between ${min} and ${max}",
  //   },
  // };
  const [selectedPeriod] = useState<number>(12);
  const [isCriteriaModalVisible, setIsCriteriaModalVisible] = useState(false);
  const [form] = Form.useForm();
  const searchParams = new URLSearchParams(location.search);
  const profileId = searchParams.get("profileId");
  const [rfmSettings, setRfmSettings] = useState<any[]>([]);
  const [brandRFMProfileSetting, setBrandRFMProfileSetting] = useState<any[]>(
    []
  );

  // Handlers

  const handleSubmitProfile = async (values: any) => {
    try {
      setLoading(true);
      const profile: BrandRFMProfile = {
        Id: selectedBrandRFMProfile ? selectedBrandRFMProfile.Id : 0,
        BrandId: brandId,
        Name: values.profile.name,
        Description: values.profile.description,
        IsActive: false,
        IsAuto: false,
        CreatedDate: new Date(),
        BrandRFMProfileSetting: brandRFMProfileSetting,
      };
      await brandRFMProfileService.updateBrandRFMProfile(profile);
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("profileId");
      navigate(`${RoutePaths.IFRAMERFMPROFILE}?${searchParams.toString()}`);
    } catch {
      console.error("Error!");
    } finally {
      setLoading(false);
    }
  };

  // =====================
  // Frequency Updates
  // =====================
  const applyFrequencyDataUpdate = (
    baseSettings: any[],
    frequencySettings: any[],
    selectedPeriod: number,
    selectedOptionPeriod?: number
  ) => {
    let newFrequencyData: any[] = [];

    if (selectedOptionPeriod !== undefined) {
      newFrequencyData = getFrequencyTiers(
        baseSettings,
        selectedOptionPeriod,
        selectedPeriod
      );
    } else {
      newFrequencyData = getFrequencyTiers(baseSettings, selectedPeriod);
    }

    const updatedSettings = frequencySettings.map((item: any) =>
      item.RFMType === 2
        ? item.Id === 0
          ? newFrequencyData.find(
              (f: any) =>
                f.RFMTier === item.RFMTier && f.RFMType === item.RFMType
            ) ?? item
          : newFrequencyData.find((f: any) => f.Id === item.Id) ?? item
        : item
    );

    const transformedData = transformRFMData(updatedSettings);
    const formValues = newFrequencyData.reduce((acc, item) => {
      acc[`${item.RFMType}-${item.RFMTier}-start`] =
        item.RFMStartValue === 0 ? "" : item.RFMStartValue;
      acc[`${item.RFMType}-${item.RFMTier}-end`] =
        item.RFMEndValue === 0 ? "" : item.RFMEndValue;
      return acc;
    }, {} as Record<string, any>);
    form.setFieldsValue(formValues);

    return {
      newFrequencyData,
      updatedSettings,
      transformedData,
      formValues,
    };
  };

  const convertBaseRanges = (
    baseRanges: TierData[],
    conversionFactor: number
  ) => {
    let previousEnd = 0;
    const isAnyDecimal = baseRanges.some((t) => (t.baseEnd ?? 0) % 1 !== 0);

    return baseRanges.map(({ tier, baseStart, baseEnd }, index) => {
      let startValue: number, endValue: number;
      if (tier === 1 && baseEnd === 0) {
        const tier2 = baseRanges.find((t) => t.tier === 2);
        startValue =
          (tier2?.baseEnd ?? 0) / conversionFactor + (isAnyDecimal ? 0.1 : 1);
        endValue = 0;
      } else if (index === 0) {
        startValue = baseStart;
        endValue = baseEnd / conversionFactor;
      } else {
        startValue = previousEnd + (isAnyDecimal ? 0.1 : 1);
        endValue = baseEnd / conversionFactor;
      }

      previousEnd = endValue;
      return { tier, baseStart: startValue, baseEnd: endValue };
    });
  };

  const scaleTiers = (baseRanges: TierData[], scaleFactor: number) => {
    let previousEnd = 0;
    const isAnyDecimal = baseRanges.some((t) => (t.baseEnd ?? 0) % 1 !== 0);

    return baseRanges.map(({ tier, baseStart, baseEnd }, index) => {
      let startValue: number, endValue: number;

      if (tier === 1 && baseEnd === 0) {
        const tier2 = baseRanges.find((t) => t.tier === 2);
        startValue =
          (tier2?.baseEnd ?? 0) * scaleFactor + (isAnyDecimal ? 0.1 : 1);
        endValue = 0;
      } else if (index === 0) {
        startValue = baseStart;
        endValue = baseEnd * scaleFactor;
      } else {
        startValue = previousEnd + (isAnyDecimal ? 0.1 : 1);
        endValue = baseEnd * scaleFactor;
      }

      previousEnd = endValue;
      return { tier, startValue, endValue };
    });
  };

  const getFrequencyTiers = (
    rfmData: any[],
    period: number,
    originalPeriod?: number
  ): any[] => {
    if (!rfmData || rfmData.length === 0) return [];

    const scaleFactor = period / 3;
    const conversionFactor = originalPeriod ? originalPeriod / 3 : 1;

    let baseRanges: TierData[] = rfmData
      .filter((item) => item.RFMType === 2)
      .sort((a, b) => b.RFMTier - a.RFMTier)
      .map(({ RFMTier, RFMStartValue, RFMEndValue }) => ({
        tier: RFMTier,
        baseStart: RFMStartValue,
        baseEnd: RFMEndValue,
      }));

    if (originalPeriod && originalPeriod !== 3) {
      baseRanges = convertBaseRanges(baseRanges, conversionFactor);
    }

    const scaledTiers = scaleTiers(baseRanges, scaleFactor);

    return rfmData.map((item) => {
      if (item.RFMType === 2) {
        const matchedTier = scaledTiers.find((t) => t.tier === item.RFMTier);
        return {
          ...item,
          RFMStartValue: matchedTier?.startValue ?? item.RFMStartValue,
          RFMEndValue: matchedTier?.endValue ?? item.RFMEndValue,
        };
      }
      return item;
    });
  };

  const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1024;
  const isMobile = window.innerWidth < 768;
  const hasValidRFM =
    rfmSettings &&
    rfmSettings.length === 3 &&
    rfmSettings.some((setting) =>
      Object.values(setting || {}).some(
        (val) => val !== undefined && val !== null
      )
    );

  const transposedData = hasValidRFM
    ? [
        {
          key: "VeryHigh",
          definition: "Very High",
          Recency: rfmSettings?.[0]?.VeryHigh ?? "-",
          Frequency: rfmSettings?.[1]?.VeryHigh ?? "-",
          Monetary: rfmSettings?.[2]?.VeryHigh ?? "-",
        },
        {
          key: "High",
          definition: "High",
          Recency: rfmSettings?.[0]?.High ?? "-",
          Frequency: rfmSettings?.[1]?.High ?? "-",
          Monetary: rfmSettings?.[2]?.High ?? "-",
        },
        {
          key: "Medium",
          definition: "Medium",
          Recency: rfmSettings?.[0]?.Medium ?? "-",
          Frequency: rfmSettings?.[1]?.Medium ?? "-",
          Monetary: rfmSettings?.[2]?.Medium ?? "-",
        },
        {
          key: "Low",
          definition: "Low",
          Recency: rfmSettings?.[0]?.Low ?? "-",
          Frequency: rfmSettings?.[1]?.Low ?? "-",
          Monetary: rfmSettings?.[2]?.Low ?? "-",
        },
        {
          key: "VeryLow",
          definition: "Very Low",
          Recency: rfmSettings?.[0]?.VeryLow ?? "-",
          Frequency: rfmSettings?.[1]?.VeryLow ?? "-",
          Monetary: rfmSettings?.[2]?.VeryLow ?? "-",
        },
      ]
    : [];

  const columnsInTablet: ColumnsType<any> = [
    {
      title: "Definition",
      dataIndex: "definition",
      key: "definition",
      fixed: "left",
      width: 150,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#C9C7C7",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      onCell: (record: any) => ({
        style: {
          backgroundColor:
            record.definition === "Very High"
              ? "#258FDA"
              : record.definition === "High"
              ? "#4BB464"
              : record.definition === "Medium"
              ? "#FFE500"
              : record.definition === "Low"
              ? "#FF9200"
              : record.definition === "Very Low"
              ? "#D92636"
              : "#F2F2F2",
          padding: "12px",
          height: "100%",
          fontWeight: "bold",
        },
      }),
    },
    {
      title: "Recency",
      dataIndex: "Recency",
      key: "recency",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#C9C7C7",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      onCell: (record: any) => ({
        style: {
          backgroundColor:
            record.definition === "Very High"
              ? "#E9F4FB"
              : record.definition === "High"
              ? "#EDF8F0"
              : record.definition === "Medium"
              ? "#FFFCE5"
              : record.definition === "Low"
              ? "#FFF4E5"
              : record.definition === "Very Low"
              ? "#FBE9EB"
              : "#F2F2F2",
          padding: "12px",
          height: "100%",
          fontWeight: record.Recency === "Out Of Range" ? "bold" : "unset",
        },
      }),
    },
    {
      title: "Frequency",
      dataIndex: "Frequency",
      key: "frequency",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#C9C7C7",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      onCell: (record: any) => ({
        style: {
          backgroundColor:
            record.definition === "Very High"
              ? "#E9F4FB"
              : record.definition === "High"
              ? "#EDF8F0"
              : record.definition === "Medium"
              ? "#FFFCE5"
              : record.definition === "Low"
              ? "#FFF4E5"
              : record.definition === "Very Low"
              ? "#FBE9EB"
              : "#F2F2F2",
          padding: "12px",
          height: "100%",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          <div style={{ fontSize: "10px", color: "#555" }}>
            every {selectedPeriod} months
          </div>
        </div>
      ),
    },
    {
      title: "Monetary",
      dataIndex: "Monetary",
      key: "monetary",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#C9C7C7",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      onCell: (record: any) => ({
        style: {
          backgroundColor:
            record.definition === "Very High"
              ? "#E9F4FB"
              : record.definition === "High"
              ? "#EDF8F0"
              : record.definition === "Medium"
              ? "#FFFCE5"
              : record.definition === "Low"
              ? "#FFF4E5"
              : record.definition === "Very Low"
              ? "#FBE9EB"
              : "#F2F2F2",
          padding: "12px",
          height: "100%",
        },
      }),
    },
  ];

  const columns: ColumnsType<{
    key: string;
    definition: string;
    veryHigh: string;
    high: string;
    medium: string;
    low: string;
    veryLow: string;
  }> = [
    {
      title: "Definition",
      dataIndex: "definition",
      key: "definition",
      fixed: "left",
      width: 150,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#C9C7C7",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      onCell: (value: any) => ({
        style: {
          backgroundColor: "#F2F2F2",
          padding: "12px",
          height: "100%",
        },
      }),
    },
    {
      title: "Very High",
      dataIndex: "VeryHigh",
      key: "veryHigh",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#258FDA",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.definition === "Frequency" && (
            <div style={{ fontSize: 12, color: "#555" }}>
              every {selectedPeriod} months
            </div>
          )}
        </div>
      ),
      onCell: (value: any) => ({
        style: {
          backgroundColor: "#E9F4FB",
          padding: "12px",
          height: "100%",
          fontWeight: value.VeryHigh === "Out Of Range" ? "bold" : "normal",
        },
      }),
    },
    {
      title: "High",
      dataIndex: "High",
      key: "high",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#4BB464",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.definition === "Frequency" && (
            <div style={{ fontSize: 12, color: "#555" }}>
              every {selectedPeriod} months
            </div>
          )}
        </div>
      ),
      onCell: (value: any) => ({
        style: {
          backgroundColor: "#EDF8F0",
          padding: "12px",
          height: "100%",
          fontWeight: value.High === "Out Of Range" ? "bold" : "normal",
        },
      }),
    },
    {
      title: "Medium",
      dataIndex: "Medium",
      key: "medium",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#FFE500",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.definition === "Frequency" && (
            <div style={{ fontSize: 12, color: "#555" }}>
              every {selectedPeriod} months
            </div>
          )}
        </div>
      ),
      onCell: (value: any) => ({
        style: {
          backgroundColor: "#FFFCE5",
          padding: "12px",
          height: "100%",
          fontWeight: value.Medium === "Out Of Range" ? "bold" : "normal",
        },
      }),
    },
    {
      title: "Low",
      dataIndex: "Low",
      key: "low",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#FF9200",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.definition === "Frequency" && (
            <div style={{ fontSize: 12, color: "#555" }}>
              every {selectedPeriod} months
            </div>
          )}
        </div>
      ),
      onCell: (value: any) => ({
        style: {
          backgroundColor: "#FFF4E5",
          padding: "12px",
          height: "100%",
          fontWeight: value.Low === "Out Of Range" ? "bold" : "normal",
        },
      }),
    },
    {
      title: "Very Low",
      dataIndex: "VeryLow",
      key: "veryLow",
      width: 180,
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#D92636",
          fontWeight: "bold",
          textAlign: "center",
        },
      }),
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.definition === "Frequency" && (
            <div style={{ fontSize: 12, color: "#555" }}>
              every {selectedPeriod} months
            </div>
          )}
        </div>
      ),
      onCell: (value: any) => {
        return {
          style: {
            backgroundColor: "#FBE9EB",
            padding: "12px",
            height: "100%",
            fontWeight: value.VeryLow === "Out Of Range" ? "bold" : "normal",
          },
        };
      },
    },
  ];

  const applyProfileSettings = (profile: any) => {
    if (!profile) return;

    const { transformedData } = applyFrequencyDataUpdate(
      profile.BrandRFMProfileSetting,
      profile.BrandRFMProfileSetting,
      selectedPeriod
    );

    setRfmSettings(transformedData);
  };

  const makeDefaultProfile = async (
    brandRFMProfileSetting: BrandRFMSetting[],
    overrides: Partial<{
      Id: number;
      Name: string;
      Description: string;
    }> = {}
  ) => {
    let settings = Array.isArray(brandRFMProfileSetting)
      ? brandRFMProfileSetting
      : [];
    // if (overrides.Id === -1) {
    //   let prevEnd = -0.01;

    //   settings = settings.map((item) => {
    //     if (item.RFMType === 3) {
    //       const scaledEnd = item.RFMEndValue > 0 ? item.RFMEndValue * 3 : 0;

    //       const newStart = parseFloat((prevEnd + 0.01).toFixed(2));

    //       prevEnd = scaledEnd;

    //       return {
    //         ...item,
    //         RFMStartValue: newStart,
    //         RFMEndValue: scaledEnd,
    //       };
    //     }
    //     return item;
    //   });
    // }

    return {
      Id: overrides.Id ?? 0,
      BrandId: brandId,
      BrandRFMProfileSetting: settings,
      Name: overrides.Name ?? "",
      Description: overrides.Description ?? "",
      IsDefault: false,
      CreatedDate: new Date(),
    };
  };

  const defaultSettings = async (
    brandId: string
  ): Promise<DefaultBrandRFMSetting | null> => {
    const setting = await brandRFMSettingService.getDefaultBrandRFMSetting(
      brandId
    );
    return setting ?? null;
  };

  const fetchRFMBrandProfile = async () => {
    if (!brandId || !profileId) return;

    try {
      if (Number(profileId) > 0) {
        const response = await brandRFMProfileService.getBrandRFMProfileById(
          brandId,
          Number(profileId)
        );
        const data = Array.isArray(response) ? response[0] : response;
        if (!data) return;

        setSelectedBrandRFMProfile(data);

        form.setFieldsValue({
          profile: {
            name: data?.Name,
            description: data?.Description,
          },
        });

        applyProfileSettings(data);
      } else {
        const setting = await defaultSettings(brandId);
        let profileName = "Full Service Default Profiles";
        let serviceSetting: BrandRFMSetting[] = [];

        if (Number(profileId) === -1) {
          profileName = "Quick Service Default Profiles";
          serviceSetting = setting?.QuickService ?? [];
        } else {
          profileName = "Full Service Default Profiles";
          serviceSetting = setting?.FullService ?? [];
        }

        if (setting) {
          const profile1 = await makeDefaultProfile(serviceSetting, {
            Id: Number(profileId) ?? 0,
            Name: profileName,
            Description: profileName,
          });

          // setSelectedBrandRFMProfile(profile1);
          applyProfileSettings(profile1);
        }
      }
    } catch (error) {
      console.error("Error fetching and applying RFM profile:", error);
    }
  };

  useEffect(() => {
    if (!brandId) return;

    if (profileId) {
      fetchRFMBrandProfile();
    } else if (selectedBrandRFMProfile) {
      applyProfileSettings(selectedBrandRFMProfile);
    }
  }, [brandId]);

  // =====================
  // Data Transformation
  // =====================
  const transformRFMData = (data: any[]) => {
    return Object.values(RFMType)
      .filter((type) => typeof type === "number")
      .map((type) => {
        const rowData: any = {
          key: type,
          definition: RFMType[type as number],
        };

        Object.values(RFMTier)
          .filter((tier) => typeof tier === "number")
          .forEach((tier) => {
            const setting = data.find(
              (item) => item.RFMType === type && item.RFMTier === tier
            );

            let formattedValue = "-";
            let startValue = 0;
            let endValue = 0;
            if (setting) {
              startValue = parseFloat(setting.RFMStartValue);
              endValue = parseFloat(setting.RFMEndValue);

              if (tier === RFMTier.VeryHigh) {
                if (type === RFMType.Frequency) {
                  formattedValue =
                    endValue === 0
                      ? `Order Count: ${
                          Number.isInteger(startValue)
                            ? startValue
                            : startValue.toFixed(1)
                        }+`
                      : `Order Count: ${
                          Number.isInteger(startValue)
                            ? startValue
                            : startValue.toFixed(1)
                        } - ${
                          Number.isInteger(endValue)
                            ? endValue
                            : endValue.toFixed(1)
                        }`;
                } else if (type === RFMType.Monetary) {
                  formattedValue = `Total Spending: $${startValue.toLocaleString(
                    "en-US"
                  )}+`;
                } else {
                  if (startValue === 0) {
                    formattedValue = `Last Order: <= ${endValue} days`;
                  } else {
                    formattedValue = `Last Order: ${startValue} - ${endValue} days`;
                  }
                }
              } else {
                if (type === RFMType.Recency) {
                  formattedValue =
                    endValue === 0
                      ? `Last Order: ${startValue}+ days`
                      : `Last Order: ${startValue} - ${endValue} days`;
                } else if (type === RFMType.Frequency) {
                  if (startValue === 0) {
                    formattedValue = `Order Count: <= ${endValue}`;
                  } else {
                    formattedValue =
                      startValue === endValue
                        ? `Order Count: ${
                            Number.isInteger(startValue)
                              ? startValue
                              : startValue.toFixed(1)
                          }`
                        : `Order Count: ${
                            Number.isInteger(startValue)
                              ? startValue
                              : startValue.toFixed(1)
                          } - ${
                            Number.isInteger(endValue)
                              ? endValue
                              : endValue.toFixed(1)
                          }`;
                  }
                } else if (type === RFMType.Monetary) {
                  if (startValue === 0) {
                    formattedValue = `Total Spending: <= $${endValue.toLocaleString(
                      "en-US"
                    )}`;
                  } else {
                    formattedValue = `Total Spending: $${startValue.toLocaleString(
                      "en-US"
                    )} - $${endValue.toLocaleString("en-US")}`;
                  }
                }
              }
            }

            rowData[RFMTier[tier as number]] = formattedValue;
          });

        return rowData;
      });
  };

  useEffect(() => {
    if (selectedBrandRFMProfile) {
      setBrandRFMProfileSetting(selectedBrandRFMProfile.BrandRFMProfileSetting);
    }
  }, [selectedBrandRFMProfile]);

  return (
    <>
      {
        <>
          <Form
            name="form-view-profile"
            onFinish={handleSubmitProfile}
            // validateMessages={validateMessages}
            initialValues={{
              profile: {
                name: selectedBrandRFMProfile?.Name,
                description: selectedBrandRFMProfile?.Description,
              },
            }}
          >
            <Card
              title={
                <Row
                  justify="space-between"
                  align="middle"
                  wrap
                  style={{ width: "100%" }}
                >
                  <Col xs={12} md={12} xl={12}>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        display: "block",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {`Profile`}
                    </span>
                  </Col>
                  <Col
                    xs={12}
                    md={12}
                    xl={12}
                    style={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <Button
                      size="middle"
                      color="primary"
                      variant="outlined"
                      style={{ height: 40, width: 150 }}
                      onClick={() => {
                        const searchParams = new URLSearchParams(
                          location.search
                        );
                        searchParams.delete("profileId");
                        navigate(
                          `${
                            RoutePaths.IFRAMERFMPROFILE
                          }?${searchParams.toString()}`
                        );
                      }}
                    >
                      Back
                    </Button>
                  </Col>
                </Row>
              }
              style={{
                display: isCriteriaModalVisible ? "none" : "block",
              }}
            >
              <Form.Item shouldUpdate noStyle>
                <Row gutter={16} align="top">
                  <Col span={12}>
                    <span>Name</span>
                  </Col>
                  <Col span={12}>
                    <span>{selectedBrandRFMProfile?.Name}</span>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                <Row gutter={16} align="top" style={{ marginTop: 10 }}>
                  <Col span={12}>
                    <span>Description</span>
                  </Col>
                  <Col span={12}>
                    <span>{selectedBrandRFMProfile?.Description}</span>
                  </Col>
                </Row>
              </Form.Item>
            </Card>
            <Card
              title={
                <Row
                  justify="space-between"
                  align="middle"
                  wrap
                  style={{ width: "100%" }}
                >
                  <Col xs={24} md={24} xl={24}>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        display: "block",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {`Configuration RFM Criteria`}
                    </span>
                  </Col>
                </Row>
              }
              style={{
                display: isCriteriaModalVisible ? "none" : "block",
              }}
            >
              {isTablet || isMobile ? (
                <Table
                  className="criteria-table"
                  columns={columnsInTablet}
                  dataSource={transposedData}
                  pagination={false}
                  scroll={{ x: "max-content" }}
                  locale={{
                    emptyText: (
                      <Empty
                        description="There are no criteria yet. Add a new criteria to get started!"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      >
                        <Button
                          type="primary"
                          onClick={() => setIsCriteriaModalVisible(true)}
                        >
                          <PlusCircleOutlined />
                          Add Criteria
                        </Button>
                      </Empty>
                    ),
                  }}
                />
              ) : (
                <Table
                  className="criteria-table"
                  columns={columns}
                  dataSource={rfmSettings}
                  pagination={false}
                  scroll={{ x: "max-content" }}
                  locale={{
                    emptyText: (
                      <Empty
                        description="There are no criteria yet. Add a new criteria to get started!"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      >
                        <Button
                          type="primary"
                          onClick={() => setIsCriteriaModalVisible(true)}
                        >
                          <PlusCircleOutlined />
                          Add Criteria
                        </Button>
                      </Empty>
                    ),
                  }}
                />
              )}
              {profileId && Number(profileId) > 0 && (
                <Row gutter={16} style={{ marginTop: 10 }}>
                  <Col span={12}></Col>
                  <Col
                    xs={24}
                    md={24}
                    xl={24}
                    style={{
                      display: "flex",
                      justifyContent: isMobile ? "center" : "flex-end",
                      gap: 8,
                    }}
                  >
                    <Button
                      size="middle"
                      type="primary"
                      style={{ height: 40, width: 150 }}
                      loading={loading}
                      onClick={() => {
                        navigate(
                          `${
                            RoutePaths.IFRAMEEDITRFMPROFILE
                          }?${searchParams.toString()}`
                        );
                      }}
                    >
                      Edit
                    </Button>
                  </Col>
                </Row>
              )}
            </Card>
          </Form>
        </>
      }
    </>
  );
};

export default EditRFMProfile;
